import { Carousel } from '@components/molecules/Carousel/Carousel';
import { ListicleItem } from '@components/molecules/ListicleItem/ListicleItem';
import { ListicleListItem } from '@components/molecules/ListicleListItem/ListicleListItem';
import {
  GetChannelShowtimeListDocument,
  GetEntitiesDocument,
  type PageContentElementListicles,
} from '@lib/graphql/generated';
import { fetchChannels } from '@lib/graphql/helpers/fetchChannels';
import {
  collectionEntryListicleQuery,
  episodeListicleQuery,
  genreListicleQuery,
  movieListicleQuery,
  personListicleQuery,
  providerListicleQuery,
  showListicleQuery,
  vodListicleQuery,
} from '@lib/graphql/helpers/fetchListicleData';
import { ssrQuery } from '@lib/graphql/urql';
import type { Movie, Show } from '@lib/streaming';
import { useFavoritesStore } from '@src/stores/favorites';
import { sortChannelsbyFavorites, withChannelsFirst } from '@utils/common';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';

export interface ListiclesProps {
  listicle_type: PageContentElementListicles['listicle_type'];
  listicle_sorting_type?: 'ranking' | 'numeration';
  listicle_sorting_direction?: 'asc' | 'desc';
  limit?: number;
  genre_filter?: string[];
  items?: string[];
  provider_filter?: string[];
  query: string;
  searchterm?: string;
  isArticle?: boolean;
  display?: 'scrollable' | 'list';
  itemData?: [Movie | Show];
  dataSet: [];
  onlyWithAsset?: boolean;
  onlyPrimetime?: boolean;
  showInitially?: boolean;
}

const fetchListicles = async (listicle_type, limit, query, items, onlyWithAsset, provider_filter, searchterm) => {
  const queries = {
    MOVIE: movieListicleQuery({ limit, query, items, onlyWithAsset }),
    SHOW: showListicleQuery({ limit, query, items, onlyWithAsset }),
    EPISODE: episodeListicleQuery({ limit }),
    GENRE: genreListicleQuery({ limit }),
    PROVIDER: providerListicleQuery({ limit, query, items }),
    VOD: vodListicleQuery({ limit, query, items, provider_filter, searchterm }),
    PERSON: personListicleQuery({ limit }),
    COLLECTION_ENTRY: collectionEntryListicleQuery({ limit, items, onlyWithAsset }),
    // biome-ignore lint/correctness/noComponentHookFactories: query map entry keyed by entity type, not a component
    MANUAL: async () => {
      const { data } = await ssrQuery({
        query: GetEntitiesDocument,
        variables: { ids: items, search: null, first: 999, page: 1, onlyWithAsset },
      });
      return data?.entities;
    },
    // biome-ignore lint/correctness/noComponentHookFactories: query map entry keyed by entity type, not a component
    CHANNEL: async () => {
      const allChannels = await fetchChannels();

      if (query === 'tv' || query === 'settings') {
        const favoriteChannelIds = useFavoritesStore
          .getState()
          .getTypeFavorites('TVChannel')
          .map((favorite) => favorite.entity?.id)
          .filter(Boolean);
        const laneChannels = query === 'settings' ? withChannelsFirst(allChannels.data, items) : allChannels.data;
        const sortedChannelsByFavorites = sortChannelsbyFavorites<
          (typeof allChannels.data)[number] & { showtimeData?: (typeof showtimeData)[number] }
        >(laneChannels, favoriteChannelIds);

        const { data } = await ssrQuery({
          query: GetChannelShowtimeListDocument,
          variables: { ids: sortedChannelsByFavorites?.map((e) => e.id)?.slice(0, limit < 2 ? limit : 2) },
        });
        const showtimeData = data?.channelShowtimeList;

        sortedChannelsByFavorites?.slice(0, limit < 2 ? limit : 2).map((channel) => {
          channel.showtimeData = showtimeData?.find((e) => e.channel.id === channel.id);
          return channel;
        });

        // Full list: the component orders by favorites and cuts to the lane size.
        return { data: sortedChannelsByFavorites };
      } else if (query === 'manual') {
        const channels = [];
        if (items?.length < 1) return;
        items.forEach((e) => {
          const channel = allChannels.data.find((s) => s.id === e);
          if (channel) {
            channels.push(channel);
          }
        });
        return { data: channels };
      } else {
        const filteredSortedChannels = allChannels.data;
        return { data: filteredSortedChannels.slice(0, limit) };
      }
    },
  };

  return queries[listicle_type]();
};

export const Listicles = ({
  listicle_type,
  listicle_sorting_type,
  listicle_sorting_direction,
  limit,
  query,
  isArticle,
  display,
  items,
  itemData,
  dataSet,
  onlyWithAsset,
  onlyPrimetime,
  showInitially,
  provider_filter,
  searchterm,
}: ListiclesProps) => {
  const [entries, setEntries] = useState({ data: [] });
  const [lastScroll, setLastScroll] = useState(0);
  const [noFetch, setNoFetch] = useState(true);
  // "TV now" lane: the SSR page is anonymous and cached for every visitor, so
  // it carries the full channel list in default order. The user's favorite
  // channels come from the persisted favorites store, which is only readable
  // in the browser; the lane stays invisible (layout kept) until then and is
  // rendered once in its final order instead of reordering after a request.
  const personalizedChannels = listicle_type === 'CHANNEL' && (query === 'tv' || query === 'settings');
  const [favoritesReady, setFavoritesReady] = useState(false);
  const { status: sessionStatus } = useSession() ?? {};
  const favorites = useFavoritesStore((state) => state.favorites);
  const favoriteChannelIds = useMemo(
    () =>
      (favorites ?? [])
        .filter((favorite) => favorite.entity?.__typename === 'TVChannel')
        .map((favorite) => favorite.entity?.id)
        .filter(Boolean),
    [favorites],
  );

  useEffect(() => {
    if (!personalizedChannels) return;
    if (useFavoritesStore.persist.hasHydrated()) setFavoritesReady(true);
    return useFavoritesStore.persist.onFinishHydration(() => setFavoritesReady(true));
  }, [personalizedChannels]);

  useEffect(() => {
    if (dataSet && dataSet?.length > 0) {
      setEntries({ data: dataSet });
      setNoFetch(true);
    } else if (items && itemData?.length > 0) {
      const entriesData = [];
      items.forEach((item) => {
        const entry = itemData.find((e) => e.id === item);
        if (entry) {
          entriesData.push(entry);
        }
      });
      if (items.length === entriesData.length) {
        setEntries({ data: entriesData });
        setNoFetch(true);
      } else {
        setNoFetch(false);
      }
    } else {
      setNoFetch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSet, itemData, items]);

  function getListicleListNumber(entries, index) {
    if (!entries.data) return null;
    if (listicle_sorting_direction === 'asc') {
      return index + 1;
    } else {
      return entries.data.length - index;
    }
  }

  const foundItems = itemData?.filter((e) => items.includes(e.id));
  const loadedEntries = entries?.data?.length > 0 ? entries : { data: foundItems?.length > 0 ? foundItems : dataSet };
  const laneSize = limit > 0 ? limit : undefined;
  // Without favorites a `settings` lane shows exactly the editor's channels.
  const defaultLaneSize =
    query === 'settings' && items?.length ? Math.min(items.length, laneSize ?? items.length) : laneSize;
  // Persisted favorites can outlive the session (they are cleared once it is
  // known to be unauthenticated), so they only count for a logged-in user.
  const laneReady = favoritesReady && sessionStatus !== 'loading';
  const hasFavoriteChannels = laneReady && sessionStatus === 'authenticated' && favoriteChannelIds.length > 0;
  const listicleEntries = personalizedChannels
    ? {
        data: hasFavoriteChannels
          ? sortChannelsbyFavorites(loadedEntries.data ?? [], favoriteChannelIds).slice(0, laneSize)
          : (loadedEntries.data ?? []).slice(0, defaultLaneSize),
      }
    : loadedEntries;

  return (
    <InView
      className={twMerge(personalizedChannels && !laneReady && 'invisible')}
      onChange={(inView) => {
        if (inView && !noFetch && dataSet.length < 1) {
          fetchListicles(listicle_type, limit, query, items, onlyWithAsset, provider_filter, searchterm).then((res) => {
            setEntries(res);
          });
        }
      }}
    >
      {isArticle ? (
        display === 'list' ? (
          <div className="space-y-16 content-auto" style={{ containIntrinsicSize: '288px' }}>
            {listicleEntries?.data?.length > 0 &&
              listicleEntries.data.map((entry, index) => (
                <div key={`${entry.id}${index}`} className="relative w-full">
                  <div className="space-y-16">
                    <ListicleListItem
                      entry={entry}
                      type={entry?.type || entry.__typename || listicle_type}
                      sorting_type={listicle_sorting_type}
                      style={listicleEntries?.data?.length > 1 ? 'compact' : 'default'}
                      place={getListicleListNumber(listicleEntries, index)}
                    />
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="pb-36 content-auto content-element-padding" style={{ containIntrinsicSize: '288px' }}>
            <div className="tvm-scrollbar -my-36 w-full overflow-x-auto py-36 pb-24">
              <div className="flex gap-4">
                {listicleEntries?.data?.length > 0 &&
                  listicleEntries.data.map((entry, index) => (
                    <ListicleItem
                      key={entry.id || index}
                      entry={entry}
                      listicleType={entry.type || entry.__typename || listicle_type}
                      query={query}
                      showInitially={showInitially && index < 5}
                    />
                  ))}
              </div>
            </div>
          </div>
        )
      ) : (
        <Carousel
          onScrollComplete={() => {
            setLastScroll((prev) => prev + 1);
          }}
          classProps={{
            carousel: twMerge(listicle_type === 'PERSON' && 'grid grid-flow-col grid-rows-2 justify-start'),
            slide: '!h-auto',
          }}
        >
          {listicleEntries.data.map((entry, index) => (
            <ListicleItem
              lastScroll={lastScroll}
              key={entry.id}
              entry={entry}
              query={query}
              listicleType={
                entry.type || (listicle_type === 'MANUAL' ? entry.__typename : listicle_type) || entry.__typename
              }
              primetime={onlyPrimetime}
              showInitially={showInitially && index < 5}
            />
          ))}
        </Carousel>
      )}
    </InView>
  );
};
